import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Leaderboard as BenchmarkIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../contexts/AuthContext';
import { CATEGORICAL_DARK, CATEGORICAL_LIGHT, NEUTRAL_DARK, NEUTRAL_LIGHT } from '../../utils/chartPalette';

interface Option {
  id: string;
  name: string;
}

interface FarmOption extends Option {
  businessName: string;
}

// See api/DTOs/BenchmarkDto.cs and api/Services/Reports/BenchmarkReportService.cs.
interface Scope {
  label: string;
  farmCount: number;
  fieldCount: number;
  plantedHa: number;
  yields: YieldRow[];
  insecticideOperations: number;
  herbicideOperations: number;
  glyphosateOperations: number;
  defoliationOperations: number;
  insecticidePerField: number;
  herbicidePerField: number;
  glyphosatePerField: number;
  defoliationPerField: number;
}

interface YieldRow {
  irrigation: string;
  balesPerHa?: number;
  fieldCount: number;
  plantedFieldCount: number;
  ha: number;
}

interface Share {
  name: string;
  geneStack?: string;
  farmHa: number;
  regionHa: number;
  industryHa: number;
}

interface Report {
  businessName: string;
  farmName: string;
  seasonName: string;
  region?: string;
  regions: string[];
  farm: Scope;
  regionScope: Scope;
  industry: Scope;
  geneStacks: Share[];
  varieties: Share[];
}

// The service's names for its gap buckets.
const NO_GENE_STACK = 'No gene stack';

const SCOPE_KEYS = ['farm', 'regionScope', 'industry'] as const;
const SHARE_KEYS = ['farmHa', 'regionHa', 'industryHa'] as const;

const OPERATIONS = [
  { label: 'Insecticide', perField: (s: Scope) => s.insecticidePerField, total: (s: Scope) => s.insecticideOperations },
  { label: 'Herbicide', perField: (s: Scope) => s.herbicidePerField, total: (s: Scope) => s.herbicideOperations },
  { label: 'Glyphosate', perField: (s: Scope) => s.glyphosatePerField, total: (s: Scope) => s.glyphosateOperations },
  { label: 'Defoliation', perField: (s: Scope) => s.defoliationPerField, total: (s: Scope) => s.defoliationOperations },
];

const formatHa = (value: number) => `${Math.round(value).toLocaleString('en-AU')} ha`;
const yieldIn = (s: Scope, irrigation: string) => s.yields.find((y) => y.irrigation === irrigation);
const yieldCoverage = (y?: YieldRow) =>
  y && y.fieldCount > 0
    ? `${y.fieldCount.toLocaleString('en-AU')} of ${y.plantedFieldCount.toLocaleString('en-AU')} fields, ${formatHa(y.ha)}`
    : y?.plantedFieldCount
      ? `no yields (${y.plantedFieldCount.toLocaleString('en-AU')} fields)`
      : 'no fields';
const formatShare = (part: number, whole: number) => {
  if (whole <= 0) return '-';
  const pct = (100 * part) / whole;
  return part > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`;
};
const share = (part: number, whole: number) => (whole > 0 ? (100 * part) / whole : 0);

const Section: React.FC<{ title: string; note?: string; children: React.ReactNode }> = ({ title, note, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
    {note && <Typography variant="caption" color="text.secondary" display="block">{note}</Typography>}
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Paper>
);

// Admin → Reports → Benchmarking: a farm's season beside its region and the
// whole industry — gene stacks and varieties as shares of planted ha, and
// insecticide / herbicide / glyphosate / defoliation operations per planted
// field.
// Exports to Excel (server-built) or PDF (built here with jsPDF).
const BenchmarkReport: React.FC = () => {
  const auth = useAuth();
  const theme = useTheme();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);
  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [farms, setFarms] = useState<FarmOption[]>([]);
  const [farmId, setFarmId] = useState('');
  const [seasons, setSeasons] = useState<Option[]>([]);
  const [seasonId, setSeasonId] = useState('');
  const [region, setRegion] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dark = theme.palette.mode === 'dark';
  const categorical = dark ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;
  const neutral = dark ? NEUTRAL_DARK : NEUTRAL_LIGHT;
  const surface = theme.palette.background.paper;
  const gridColor = theme.palette.divider;

  useEffect(() => {
    if (!auth.isSignedIn || !auth.tokens?.accessToken) return;
    fetch('/api/businesses/list', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: any[]) => setBusinesses(data.map((b) => ({ id: b.id, name: b.name }))))
      .catch(() => setBusinesses([]));
  }, [auth.isSignedIn, auth.tokens?.accessToken, authHeaders]);

  // A business's farms (and its child businesses'), and the seasons it has
  // records in, most recent first.
  useEffect(() => {
    setFarms([]);
    setFarmId('');
    setSeasons([]);
    setSeasonId('');
    setReport(null);
    if (!businessId) return;
    let cancelled = false;
    (async () => {
      const [farmRes, seasonRes] = await Promise.all([
        fetch(`/api/businesses/${businessId}/records/farms`, { headers: authHeaders() }),
        fetch(`/api/reports/available-time-periods?businessId=${businessId}`, { headers: authHeaders() }),
      ]);
      if (cancelled) return;
      const farmList: FarmOption[] = farmRes.ok ? await farmRes.json() : [];
      const seasonList: Option[] = seasonRes.ok ? await seasonRes.json() : [];
      setFarms(farmList);
      setFarmId(farmList.length === 1 ? farmList[0].id : '');
      setSeasons(seasonList);
      setSeasonId(seasonList[0]?.id ?? '');
    })();
    return () => { cancelled = true; };
  }, [businessId, authHeaders]);

  const params = useCallback(() => {
    const p = new URLSearchParams({ farmId, timePeriodId: seasonId });
    if (region) p.set('region', region);
    return p.toString();
  }, [farmId, seasonId, region]);

  useEffect(() => {
    if (!farmId || !seasonId) {
      setReport(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetch(`/api/benchmark?${params()}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Failed to load the benchmark');
        const data: Report = await res.json();
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [farmId, seasonId, params, authHeaders]);

  const scopeLabels = useMemo(
    () => (report ? [`Farm: ${report.farmName}`, `Region: ${report.region ?? '-'}`, 'Industry'] : []),
    [report],
  );

  const handleExcel = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch(`/api/benchmark/file?${params()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');
      const disposition = res.headers.get('Content-Disposition') || '';
      const fileName = /filename="?([^";]+)"?/.exec(disposition)?.[1] || 'benchmark.xlsx';
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDownloading(false);
    }
  };

  const handlePdf = () => {
    if (!report) return;
    const scopes = SCOPE_KEYS.map((k) => report[k]);
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const left = 14;
    doc.setFontSize(14);
    doc.text(`Benchmark: ${report.farmName} (${report.businessName})`, left, 16);
    doc.setFontSize(10);
    doc.text(`${report.seasonName}, compared with ${report.region ?? 'no region'} and the industry`, left, 22);

    const tableStyle = { styles: { fontSize: 8 }, headStyles: { fillColor: [42, 120, 214] as [number, number, number] }, margin: { left } };
    const heading = (text: string, y: number) => {
      doc.setFontSize(11);
      doc.text(text, left, y);
      return y + 2;
    };
    const after = () => ((doc as any).lastAutoTable?.finalY ?? 30) + 10;

    autoTable(doc, {
      ...tableStyle,
      startY: heading('Yield, bales/ha, area-weighted', 30),
      head: [['', ...scopeLabels]],
      body: report.industry.yields.map(({ irrigation }) => [
        irrigation,
        ...scopes.map((s) => {
          const y = yieldIn(s, irrigation);
          return y?.balesPerHa != null ? `${y.balesPerHa.toFixed(2)} (${yieldCoverage(y)})` : `- (${yieldCoverage(y)})`;
        }),
      ]),
    });
    autoTable(doc, {
      ...tableStyle,
      startY: heading('Operations per planted field, farm average', after()),
      head: [['', ...scopeLabels]],
      body: [
        ['Farms', ...scopes.map((s) => s.farmCount.toLocaleString('en-AU'))],
        ['Planted fields', ...scopes.map((s) => s.fieldCount.toLocaleString('en-AU'))],
        ['Planted ha', ...scopes.map((s) => formatHa(s.plantedHa))],
        ...OPERATIONS.map((o) => [`${o.label} operations per field`, ...scopes.map((s) => `${o.perField(s).toFixed(2)} (${o.total(s).toLocaleString('en-AU')})`)]),
      ],
    });
    const shareTable = (title: string, rows: Share[], withGeneStack: boolean) => {
      autoTable(doc, {
        ...tableStyle,
        startY: heading(title, after()),
        head: [[withGeneStack ? 'Variety' : 'Gene stack', ...(withGeneStack ? ['Gene stack'] : []), ...scopeLabels]],
        body: rows.map((r) => [
          r.name,
          ...(withGeneStack ? [r.geneStack ?? ''] : []),
          ...SHARE_KEYS.map((k, i) => (r[k] > 0 ? `${formatShare(r[k], scopes[i].plantedHa)} (${formatHa(r[k])})` : '-')),
        ]),
      });
    };
    shareTable('Gene stacks, share of planted ha', report.geneStacks, false);
    shareTable('Varieties, share of planted ha', report.varieties, true);

    doc.setFontSize(7);
    doc.text(
      doc.splitTextToSize(
        'Operations (a tank mix counts once) on planted fields with at least one product of the kind (insecticide, herbicide or defoliant by product category; glyphosate by active ingredient), '
        + 'divided by the farm\'s planted fields; for a region or the industry, the average of those farm figures, so each farm counts the same. '
        + 'A defoliation pass is not counted as a herbicide one. Totals in brackets. Yield: field season bales/ha, weighted by planted ha, over the fields of that irrigation type that have one. '
        + 'Region and industry include the farm.',
        180,
      ),
      left,
      after() - 4,
    );
    doc.save(`benchmark_${report.farmName}_${report.seasonName}.pdf`.replace(/[^\w.-]+/g, '_'));
  };

  // Colour follows the gene stack: the server lists them by industry ha,
  // which a farm or region pick doesn't change. The gap bucket is grey.
  const geneStackSeries = useMemo(() => {
    if (!report) return [];
    let i = 0;
    return report.geneStacks.map((g) => ({ key: g.name, color: g.name === NO_GENE_STACK ? neutral : categorical[i++] ?? neutral }));
  }, [report, categorical, neutral]);

  const scopes = report ? SCOPE_KEYS.map((k) => report[k]) : [];
  const operationsData = OPERATIONS.map((o) => ({
    measure: o.label,
    ...Object.fromEntries(scopeLabels.map((label, i) => [label, o.perField(scopes[i])])),
  }));
  const geneStackData = scopeLabels.map((label, i) => ({
    scope: label,
    ...Object.fromEntries((report?.geneStacks ?? []).map((g) => [g.name, share(g[SHARE_KEYS[i]], scopes[i].plantedHa)])),
  }));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <BenchmarkIcon />
          Benchmarking
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            disabled={!report || loading || downloading}
            onClick={handleExcel}
          >
            Excel
          </Button>
          <Button variant="outlined" startIcon={<PdfIcon />} disabled={!report || loading} onClick={handlePdf}>
            PDF
          </Button>
        </Box>
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 260, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
          options={businesses}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={businesses.find((b) => b.id === businessId) || null}
          onChange={(_, value) => setBusinessId(value?.id || null)}
          renderInput={(params) => <TextField {...params} label="Business" />}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 240, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
          options={farms}
          disabled={!businessId}
          getOptionLabel={(o) => o.name}
          groupBy={(o) => o.businessName}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={farms.find((f) => f.id === farmId) || null}
          onChange={(_, value) => { setFarmId(value?.id || ''); setRegion(null); }}
          renderInput={(params) => <TextField {...params} label="Farm" />}
        />
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={!businessId}>
          <InputLabel id="benchmark-season-label">Season</InputLabel>
          <Select labelId="benchmark-season-label" label="Season" value={seasonId} onChange={(e) => { setSeasonId(e.target.value); setRegion(null); }}>
            {seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={!report}>
          <InputLabel id="benchmark-region-label">Compare with region</InputLabel>
          <Select
            labelId="benchmark-region-label"
            label="Compare with region"
            value={report?.region ?? ''}
            onChange={(e) => setRegion(e.target.value || null)}
          >
            {(report?.regions ?? []).map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!farmId || !seasonId ? (
        <Alert severity="info">Pick a business, a farm and a season.</Alert>
      ) : loading && !report ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : report && report.farm.fieldCount === 0 ? (
        <Alert severity="info">{report.farmName} has no plantings in {report.seasonName}.</Alert>
      ) : report && (
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {report.farmName} ({report.businessName}), {report.seasonName}: {report.farm.fieldCount.toLocaleString('en-AU')} planted fields,{' '}
            {formatHa(report.farm.plantedHa)}. Compared with {report.region ?? 'no region'} ({report.regionScope.farmCount.toLocaleString('en-AU')} farms, {report.regionScope.fieldCount.toLocaleString('en-AU')} fields)
            and the industry ({report.industry.farmCount.toLocaleString('en-AU')} farms, {report.industry.fieldCount.toLocaleString('en-AU')} fields). Region and industry include this farm.
          </Typography>

          <Section
            title="Yield, bales/ha, area-weighted"
            note="Field season yields, weighted by planted ha, over the fields of each irrigation type that have one. Fields with no yield yet (or 0) are left out."
          >
            {report.industry.yields.length === 0 ? (
              <Typography variant="body2">No yields recorded for {report.seasonName} yet.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      {scopeLabels.map((label) => <TableCell key={label} align="right">{label}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.industry.yields.map(({ irrigation }) => (
                      <TableRow key={irrigation}>
                        <TableCell>{irrigation}</TableCell>
                        {scopes.map((s, i) => {
                          const y = yieldIn(s, irrigation);
                          return (
                            <TableCell key={i} align="right">
                              <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                                {y?.balesPerHa != null ? y.balesPerHa.toFixed(2) : '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">{yieldCoverage(y)}</Typography>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Section>

          <Section
            title="Operations per planted field, farm average"
            note="Operations on a planted field with at least one insecticide, herbicide or defoliant product (by category), or a glyphosate product (by active ingredient), divided by the farm's planted fields. A tank mix counts once, including one imported as a row per product (those on the same field and day are one operation). For a region or the industry, the average of those farm figures, so each farm counts the same. A defoliation pass isn't counted as a herbicide one (some defoliants are also filed as herbicides, and diuron is mixed in as an enhancer). Totals in brackets."
          >
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <Box sx={{ flex: '1 1 380px', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={operationsData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                    <XAxis dataKey="measure" />
                    <YAxis width={40} />
                    <Tooltip
                      cursor={{ fill: gridColor, opacity: 0.3 }}
                      formatter={(value: number, name: string) => [`${Number(value).toFixed(2)} per field`, name]}
                      contentStyle={{ background: surface, borderColor: gridColor }}
                    />
                    <Legend formatter={(value: string) => <span style={{ color: theme.palette.text.primary }}>{value}</span>} />
                    {scopeLabels.map((label, i) => (
                      <Bar key={label} dataKey={label} name={label} fill={categorical[i]} radius={[4, 4, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <TableContainer sx={{ flex: '1 1 380px', minWidth: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      {scopeLabels.map((label) => <TableCell key={label} align="right">{label}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Farms</TableCell>
                      {scopes.map((s, i) => <TableCell key={i} align="right">{s.farmCount.toLocaleString('en-AU')}</TableCell>)}
                    </TableRow>
                    <TableRow>
                      <TableCell>Planted fields</TableCell>
                      {scopes.map((s, i) => <TableCell key={i} align="right">{s.fieldCount.toLocaleString('en-AU')}</TableCell>)}
                    </TableRow>
                    <TableRow>
                      <TableCell>Planted ha</TableCell>
                      {scopes.map((s, i) => <TableCell key={i} align="right">{formatHa(s.plantedHa)}</TableCell>)}
                    </TableRow>
                    {OPERATIONS.map((o) => (
                      <TableRow key={o.label}>
                        <TableCell>{o.label}</TableCell>
                        {scopes.map((s, i) => (
                          <TableCell key={i} align="right">
                            {o.perField(s).toFixed(2)}
                            <Typography component="span" variant="caption" color="text.secondary"> ({o.total(s).toLocaleString('en-AU')})</Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Section>

          <Section title="Gene stacks, share of planted ha">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={geneStackData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="scope" width={170} tick={{ fontSize: 12 }} />
                <Tooltip
                  cursor={{ fill: gridColor, opacity: 0.3 }}
                  formatter={(value: number, name: string) => [Number(value) > 0 && Number(value) < 1 ? '<1%' : `${Math.round(Number(value))}%`, name]}
                  contentStyle={{ background: surface, borderColor: gridColor }}
                />
                <Legend formatter={(value: string) => <span style={{ color: theme.palette.text.primary }}>{value}</span>} />
                {geneStackSeries.map((s) => (
                  <Bar key={s.key} dataKey={s.key} name={s.key} stackId="share" fill={s.color} stroke={surface} strokeWidth={2} />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <ShareTable rows={report.geneStacks} scopes={scopes} scopeLabels={scopeLabels} nameHeader="Gene stack" />
          </Section>

          <Section title="Varieties, share of planted ha" note="The farm's varieties first, then the rest by industry share.">
            <ShareTable rows={report.varieties} scopes={scopes} scopeLabels={scopeLabels} nameHeader="Variety" withGeneStack barColors={categorical} />
          </Section>
        </Box>
      )}
    </Box>
  );
};

// Name, then each scope's share of planted ha (its ha underneath). With
// barColors, a thin bar under each share in that scope's colour.
const ShareTable: React.FC<{
  rows: Share[];
  scopes: Scope[];
  scopeLabels: string[];
  nameHeader: string;
  withGeneStack?: boolean;
  barColors?: string[];
}> = ({ rows, scopes, scopeLabels, nameHeader, withGeneStack, barColors }) => (
  <TableContainer sx={{ mt: 1 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{nameHeader}</TableCell>
          {withGeneStack && <TableCell>Gene stack</TableCell>}
          {scopeLabels.map((label) => <TableCell key={label} align="right" sx={{ minWidth: 120 }}>{label}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.name}>
            <TableCell sx={{ fontWeight: r.farmHa > 0 ? 600 : undefined }}>{r.name}</TableCell>
            {withGeneStack && <TableCell>{r.geneStack ?? '-'}</TableCell>}
            {SHARE_KEYS.map((k, i) => (
              <TableCell key={k} align="right">
                {r[k] > 0 ? formatShare(r[k], scopes[i].plantedHa) : '-'}
                {r[k] > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">{formatHa(r[k])}</Typography>
                )}
                {barColors && r[k] > 0 && (
                  <Box sx={{ height: 4, borderRadius: '0 2px 2px 0', bgcolor: barColors[i], width: `${share(r[k], scopes[i].plantedHa)}%` }} />
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export default BenchmarkReport;
